import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/customs/modal";
import { Textarea } from "@/components/ui/textarea";
import {
  CustomQuotationUpdateBoFormData,
  FinalizeExpeditionFormData,
  QuotationUpdateBoFormData,
} from "@/types/forms/bo/expeditionBo.form";
import { useFormContext } from "react-hook-form";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { isAxiosError } from "axios";
import {
  updatePreFinalizeCustomQuotationFedexPayload,
  updatePreFinalizeCustomQuotationSimplifiedPayload,
  updatePreFinalizeCustomQuotationPayload,
  updatePreFinalizeFedexQuotationPayload,
  updatePreFinalizeWoApiQuotationPayload,
} from "../expeditionForm/expeditionFormHelper";
import {
  useUpdateFinalizeQuotation,
  useUpdatePreFinalizeCustomQuotationBo,
  useUpdatePreFinalizeQuotation,
} from "@/api/expedition.api";
import { useCreateLogBo } from "@/api/log.api";
import { CreateLogBoDto } from "@/types/api/api-types";
import { ExpeditionTypeEnum } from "@/constants/enums/expeditionType.enum";
import { useTranslation } from "react-i18next";

type SaveDraftModalShellProps = {
  open: boolean;
  onClose: () => void;
  expeditionId: string;
  onSave: (note?: string) => Promise<void>;
};

const serializeSaveDraftError = (error: unknown): CreateLogBoDto["details"] => {
  if (!isAxiosError(error)) {
    return { message: error instanceof Error ? error.message : String(error) };
  }

  return {
    message: error.message,
    code: error.code ?? null,
    status: error.response?.status ?? null,
    method: error.config?.method ?? null,
    url: error.config?.url ?? null,
    // Corps de requête déjà sérialisé par axios.
    requestBody: error.config?.data ?? null,
    responseBody: error.response?.data ?? null,
  };
};

type SaveDraftModalProps = SaveDraftModalStepProps & {
  formStep: number;
  expeditionType: ExpeditionTypeEnum;
};

type SaveDraftModalStepProps = {
  open: boolean;
  onClose: () => void;
  expeditionId: string;
};

const SaveDraftModalShell = ({ open, onClose, expeditionId, onSave }: SaveDraftModalShellProps) => {
  const { t } = useTranslation(["boExpeditionForm", "common"]);
  const [note, setNote] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const { mutate: createLog } = useCreateLogBo();

  const handleClose = () => {
    if (isSaving) return;
    setNote("");
    onClose();
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(note.trim() || undefined);
      setNote("");
      onClose();
    } catch (error) {
      createLog({
        action: "EXPEDITION_DEBUG",
        entityId: expeditionId,
        details: serializeSaveDraftError(error),
      });
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      dismissible={!isSaving}
      title={t("saveDraftModal.title")}
      description={t("saveDraftModal.description")}
      footer={
        <div className="flex w-full flex-row justify-between gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            className="flex-1"
            disabled={isSaving}
          >
            {t("common:actions.cancel")}
          </Button>
          <Button type="button" onClick={handleSave} className="flex-1" disabled={isSaving}>
            {isSaving ? <Loader2 className="animate-spin" /> : t("saveDraftModal.submit")}
          </Button>
        </div>
      }
    >
      <Textarea
        autoFocus
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={t("saveDraftModal.notePlaceholder")}
        aria-label={t("saveDraftModal.noteAriaLabel")}
      />
    </Modal>
  );
};

const QuotationSaveDraftModalStepOne = ({
  open,
  onClose,
  expeditionId,
}: SaveDraftModalStepProps) => {
  const methods = useFormContext<QuotationUpdateBoFormData>();
  const { mutateAsync: updatePreFinalizeQuotation } = useUpdatePreFinalizeQuotation();

  const handleSave = async (note?: string) => {
    const data = methods.getValues();

    if (data.apiEnum === "FEDEX") {
      await updatePreFinalizeQuotation({
        id: expeditionId,
        payload: updatePreFinalizeFedexQuotationPayload({
          data: data as Extract<QuotationUpdateBoFormData, { apiEnum: "FEDEX" }>,
          note,
        }),
      });
      return;
    }

    await updatePreFinalizeQuotation({
      id: expeditionId,
      payload: updatePreFinalizeWoApiQuotationPayload({
        data: data as Extract<QuotationUpdateBoFormData, { apiEnum: "NONE" }>,
        note,
      }),
    });
  };

  return (
    <SaveDraftModalShell
      open={open}
      onClose={onClose}
      expeditionId={expeditionId}
      onSave={handleSave}
    />
  );
};

const CustomQuotationSaveDraftModalStepOne = ({
  open,
  onClose,
  expeditionId,
}: SaveDraftModalStepProps) => {
  const methods = useFormContext<CustomQuotationUpdateBoFormData>();
  const { mutateAsync: updatePreFinalizeCustomQuotation } = useUpdatePreFinalizeCustomQuotationBo();

  const handleSave = async (note?: string) => {
    const data = methods.getValues();

    if (data.apiEnum === "FEDEX") {
      await updatePreFinalizeCustomQuotation({
        id: expeditionId,
        payload: updatePreFinalizeCustomQuotationFedexPayload({
          data: data as Extract<CustomQuotationUpdateBoFormData, { apiEnum: "FEDEX" }>,
          note,
        }),
      });
      return;
    } else if (data.apiEnum === "NONE") {
      await updatePreFinalizeCustomQuotation({
        id: expeditionId,
        payload: updatePreFinalizeCustomQuotationPayload({
          data: data as Extract<CustomQuotationUpdateBoFormData, { apiEnum: "NONE" }>,
          note,
        }),
      });
      return;
    } else {
      await updatePreFinalizeCustomQuotation({
        id: expeditionId,
        payload: updatePreFinalizeCustomQuotationSimplifiedPayload({
          data: data as Extract<CustomQuotationUpdateBoFormData, { apiEnum: "NONE_SIMPLIFIED" }>,
          note,
        }),
      });
      return;
    }
  };

  return (
    <SaveDraftModalShell
      open={open}
      onClose={onClose}
      expeditionId={expeditionId}
      onSave={handleSave}
    />
  );
};

const SaveDraftModalStepOne = ({
  expeditionType,
  ...stepProps
}: SaveDraftModalStepProps & { expeditionType: ExpeditionTypeEnum }) => {
  if (expeditionType === ExpeditionTypeEnum.CUSTOM_QUOTATION) {
    return <CustomQuotationSaveDraftModalStepOne {...stepProps} />;
  }

  return <QuotationSaveDraftModalStepOne {...stepProps} />;
};

const SaveDraftModalStepTwo = ({ open, onClose, expeditionId }: SaveDraftModalStepProps) => {
  const methods = useFormContext<FinalizeExpeditionFormData>();
  const { mutateAsync: updateFinalizeQuotation } = useUpdateFinalizeQuotation();

  const handleSave = async (note?: string) => {
    const data = methods.getValues();

    await updateFinalizeQuotation({
      id: expeditionId,
      payload: { ...data, note },
    });
  };

  return (
    <SaveDraftModalShell
      open={open}
      onClose={onClose}
      expeditionId={expeditionId}
      onSave={handleSave}
    />
  );
};

export const SaveDraftModal = ({ formStep, expeditionType, ...stepProps }: SaveDraftModalProps) => {
  if (formStep === 1) {
    return <SaveDraftModalStepOne expeditionType={expeditionType} {...stepProps} />;
  }

  return <SaveDraftModalStepTwo {...stepProps} />;
};
